import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useToasts } from "react-toast-notifications";

import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { FETCH_SAVED_SEARCHES, UPSERT_SAVED_SEARCH } from "@gql/saved-searches";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type FetchSavedSearches_me_savedSearches,
  type UpsertSavedSearch,
  type UpsertSavedSearchVariables,
} from "@north-beam/nb-client-side";
import { SecondaryButton, TertiaryButton } from "@shared/buttons";

import { type SearchQuery } from "../types";

interface Props {
  target: string;
  isOpen: boolean;
  disabled: boolean;
  searchQuery: SearchQuery;
  savedSearches: FetchSavedSearches_me_savedSearches[];
  onOpenChange(value: boolean): void;
  onSave(value: FetchSavedSearches_me_savedSearches): void;
}

export const SaveSearchPopover = ({
  target,
  isOpen,
  disabled,
  searchQuery,
  savedSearches,
  onOpenChange,
  onSave,
}: Props) => {
  const [name, setName] = useState("");

  const [upsertSavedSearch, { loading }] = useNorthbeamMutation<
    UpsertSavedSearch,
    UpsertSavedSearchVariables
  >(UPSERT_SAVED_SEARCH, {
    refetchQueries: [{ query: FETCH_SAVED_SEARCHES, variables: { target } }],
    awaitRefetchQueries: true,
  });

  const { addToast } = useToasts();

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    transform: false,
    placement: "bottom-end",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const savedSearch = useMemo(() => {
    return savedSearches.find((e) => e.id === searchQuery.id);
  }, [searchQuery, savedSearches]);

  const showErrorMessage = useMemo(() => {
    if (savedSearch) {
      return (
        savedSearches.find(
          (e) => e.name === name && e.id !== savedSearch.id,
        ) !== undefined
      );
    } else {
      return savedSearches.find((e) => e.name === name) !== undefined;
    }
  }, [name, savedSearches, savedSearch]);

  useEffect(() => {
    if (isOpen) {
      setName(savedSearch ? savedSearch.name : "");
    }
  }, [isOpen, savedSearch]);

  return (
    <div className="self-stretch">
      <button
        disabled={disabled}
        className={clsx(
          "font-semibold border text-xs h-full px-3.5 rounded-r-md",
          {
            "text-gray": disabled,
            "text-cosmic+1": !disabled,
            "border-l-gray border-r-white border-y-white": !isOpen,
            "text-white border-primary bg-primary focus:bg-primary hover:bg-primary":
              isOpen,
            "hover:bg-cosmic-5 hover:border-r-cosmic-5 hover:border-y-cosmic-5":
              !isOpen && !disabled,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        SAVE
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(
              "absolute left-0 z-10 px-4 pb-4 pt-3 mt-0.5 w-full",
              "bg-white rounded-lg elevation-1 text-sm",
            )}
            {...getFloatingProps()}
          >
            <label className="text-xs text-interstellar+2 mb-1">
              SAVED SEARCH NAME
            </label>
            <div className="nb-input-container">
              <input
                className="nb-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {showErrorMessage && (
                <div className="text-xs text-mars mt-1">
                  Saved search with this name already exists!
                </div>
              )}
            </div>
            <div className="mt-2 flex justify-end">
              <TertiaryButton
                className="mr-2 font-semibold"
                onClick={() => onOpenChange(false)}
              >
                CANCEL
              </TertiaryButton>
              <SecondaryButton
                className={clsx(
                  "font-semibold min-w-[80px] h-[32px] flex justify-center items-center",
                )}
                isDisabled={loading || showErrorMessage || name === ""}
                loading={loading}
                onClick={async () => {
                  const result = await upsertSavedSearch({
                    variables: {
                      target,
                      name,
                      searchMethod: searchQuery.method,
                      searchOperator: searchQuery.operator,
                      query: searchQuery.query,
                    },
                  });

                  if (result.data) {
                    addToast("Search has been saved", {
                      appearance: "success",
                      autoDismiss: true,
                    });

                    onSave(result.data?.upsertSavedSearch);
                  } else {
                    addToast("Saving unsuccessful! Try again!", {
                      appearance: "error",
                      autoDismiss: true,
                    });
                  }
                }}
              >
                SAVE
              </SecondaryButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
